import { Opts, useReactQuery } from './api.utils';
import { UseFetchOpts, useFetch } from '../utils';
import type {
  ActiveTariff,
  BillingResponse,
  BusinessUser,
  ManagerSettings,
  Me,
  PaykickstartProduct,
  PaymentStatusResponse,
  ReferralStats,
  SupportTicket,
  SupportTicketDetailed,
  Tariff,
  TransactionsResponse,
  UsageData,
} from './types';

export const useMeQuery = useReactQuery<Me>('me');
export const useMyTariffQuery = useReactQuery<ActiveTariff>('me/tariff');
export const useReferralStatsQuery = useReactQuery<ReferralStats>('me/referral-stats');
export const useDepositsQuery = useReactQuery<PaykickstartProduct[]>('deposits');
export const useTariffsQuery = useReactQuery<Tariff[]>('tariffs');
export const useTicketsQuery = ({ updateDate = false }: { updateDate?: boolean }, opts?: Opts) =>
  useReactQuery<SupportTicket[]>(`me/tickets?updateDate=${updateDate}`)({ ...opts });
export const useTicketQuery = (ticketId: string, opts?: Opts) =>
  useReactQuery<SupportTicketDetailed>(`me/tickets/${ticketId}`)(opts);
export const useCouponTariffQuery = (code: string, opts?: Opts) =>
  useReactQuery<Tariff>(`coupon-tariff?code=${code}`)(opts);
export const useUsageDailyQuery = useReactQuery<UsageData[]>('me/usage/month');
export const useUsageMonthlyQuery = useReactQuery<UsageData[]>('me/usage/year');
export const useBusinessUsersQuery = useReactQuery<BusinessUser[]>('business/users');
export const useBusinessUserQuery = (id: number, opts?: Opts) =>
  useReactQuery<BusinessUser>(`business/users/${id}`)(opts);

export const useBusinessUsageDailyQuery = (opts?: Opts) =>
  useReactQuery<UsageData[]>(`business/usage/month`)(opts);
export const useBusinessUsageMonthlyQuery = (opts?: Opts) =>
  useReactQuery<UsageData[]>(`business/usage/year`)(opts);
export const useBusinessTransactionsQuery = (page: number, opts?: Opts) =>
  useReactQuery<TransactionsResponse>(`business/transactions?page=${page}`)(opts);

export const useBusinessUserUsageDailyQuery = (id: number, opts?: Opts) =>
  useReactQuery<UsageData[]>(`business/users/${id}/usage/month`)(opts);
export const useBusinessUserUsageMonthlyQuery = (id: number, opts?: Opts) =>
  useReactQuery<UsageData[]>(`business/users/${id}/usage/year`)(opts);
export const useBusinessUserTransactionsQuery = (id: number, page: number, opts?: Opts) =>
  useReactQuery<TransactionsResponse>(`business/users/${id}/transactions?page=${page}`)(opts);

export const useBusinessTariffsQuery = useReactQuery<Tariff[]>('business/tariffs');
export const useManagerSettingsQuery = (managerId: number, opts?: Opts) =>
  useReactQuery<ManagerSettings>(`business/managers/${managerId}/settings`)(opts);
export const useCustomTopUpFunnelQuery = useReactQuery<{ funnelId: number | null }>(
  'top-up-funnel',
);
export const useTransactionsQuery = (page = 1, opts?: Opts) =>
  useReactQuery<TransactionsResponse>(`me/transactions?page=${page}`)(opts);
export const useBilling = (page = 1, opts?: Opts) =>
  useReactQuery<BillingResponse>(`me/billing?page=${page}`)(opts);
export const useTransactionStateQuery = (id: string, productId: string, opts?: UseFetchOpts) =>
  useFetch<PaymentStatusResponse>(`payment?paymentId=${id}&productId=${productId}`, opts);
export const useChaportIdQuery = (opts?: UseFetchOpts) =>
  useFetch<{ id: string | null; hash: string }>('chaport-id', opts);
// Signed identity for the sy-support-chat widget — lets it recognize the logged-in
// Streamster user and use their account email without asking. token is null when
// the server has no shared secret configured (widget then runs anonymously).
export const useSupportChatIdentityQuery = (opts?: UseFetchOpts) =>
  useFetch<{ token: string | null; email: string | null; name: string | null }>(
    'ai-chat/identity',
    opts,
  );
